import { useState } from "react";


/**
 * VeggieIcon — stage-aware SVG illustrations + painterly watercolour PNG
 * fallback for common garden crops.
 *
 * We deliberately keep these icons tiny, flat, and line-based so they read
 * at 14–28px (bed grid cells, crop cards, dashboard stage tags). Each crop
 * in the catalog maps to one of ~14 visual keys via `visualKeyForCrop()`,
 * then the visual key × stage-bucket renders a unique silhouette:
 *
 *   seed    ← planned, sown              (tiny seed/dot in soil)
 *   sprout  ← germinated, seedling       (cotyledons + first true leaves)
 *   growing ← vegetative, flowering      (mature plant silhouette)
 *   mature  ← fruiting, harvest          (plant with its characteristic fruit/head)
 *   finished                              (mature, de-saturated)
 *
 * The icons use `currentColor` for strokes and a few inline fill tokens
 * (earthy sage / terracotta / golden) so they stay on-palette in both
 * light and dark contexts. Sized via the `size` prop.
 */

// --------------------------------------------------------------------------
// Crop-name → visual-key lookup
// --------------------------------------------------------------------------

const CROP_TO_VISUAL = {
    // solanaceae / round fruit
    "tomato": "fruit-round",
    "cherry tomato": "fruit-round",
    "aubergine": "fruit-round",
    "eggplant": "fruit-round",
    // elongated fruit
    "pepper": "fruit-long",
    "chili": "fruit-long",
    "chilli": "fruit-long",
    "courgette": "fruit-long",
    "zucchini": "fruit-long",
    "cucumber": "fruit-long",
    // cucurbits that climb/sprawl
    "pumpkin": "vine-fruit",
    "winter squash": "vine-fruit",
    "squash": "vine-fruit",
    "melon": "vine-fruit",
    // long root
    "carrot": "root-long",
    "parsnip": "root-long",
    "radish": "root-round",
    "salsify": "root-long",
    // round root
    "beetroot": "root-round",
    "beet": "root-round",
    "turnip": "root-round",
    "swede": "root-round",
    "kohlrabi": "root-round",
    "celeriac": "root-round",
    // leafy head / firm ball
    "lettuce": "leafy-head",
    "cabbage": "leafy-head",
    "cauliflower": "leafy-head",
    "pak choi": "leafy-head",
    "bok choy": "leafy-head",
    "chinese cabbage": "leafy-head",
    "lamb's lettuce": "leafy-head",
    // leafy loose
    "kale": "leafy-loose",
    "chard": "leafy-loose",
    "swiss chard": "leafy-loose",
    "spinach": "leafy-loose",
    "perpetual spinach": "leafy-loose",
    "sorrel": "leafy-loose",
    "mizuna": "leafy-loose",
    "rocket": "leafy-loose",
    "arugula": "leafy-loose",
    // branching brassica
    "broccoli": "branching",
    "brussels sprouts": "branching",
    // bulb allium
    "onion": "bulb",
    "shallot": "bulb",
    "garlic": "bulb",
    "spring onion": "stalk",
    // stalk allium / umbellifer
    "leek": "stalk",
    "chive": "stalk",
    "celery": "stalk",
    "fennel": "stalk",
    // legumes
    "pea": "legume",
    "sugar snap pea": "legume",
    "runner bean": "legume",
    "french bean": "legume",
    "broad bean": "legume",
    "bean": "legume",
    // tubers
    "potato": "potato",
    // herbs (small)
    "basil": "herb-small",
    "parsley": "herb-small",
    "coriander": "herb-small",
    "cilantro": "herb-small",
    "dill": "herb-small",
    "mint": "herb-small",
    "oregano": "herb-small",
    "marjoram": "herb-small",
    // woody herbs
    "thyme": "herb-woody",
    "rosemary": "herb-woody",
    "sage": "herb-woody",
    "lavender": "herb-woody",
    // flowers
    "nasturtium": "flower",
    "borage": "flower",
    "calendula": "flower",
    "sunflower": "flower",
    "marigold": "flower",
    // berries / soft fruit
    "strawberry": "berry",
    "raspberry": "berry",
    // perennials
    "rhubarb": "rhubarb",
};

export function visualKeyForCrop(cropName) {
    if (!cropName) return "generic";
    const key = cropName.toLowerCase().trim();
    if (CROP_TO_VISUAL[key]) return CROP_TO_VISUAL[key];
    // Fuzzy fallback: match the first word (e.g. "tomato cherry" → "tomato")
    const firstWord = key.split(/\s+/)[0];
    return CROP_TO_VISUAL[firstWord] || "generic";
}

// --------------------------------------------------------------------------
// Stage bucketing
// --------------------------------------------------------------------------

export function stageBucket(stage) {
    switch (stage) {
        case "planned":
        case "sown":
            return "seed";
        case "germinated":
        case "seedling":
            return "sprout";
        case "vegetative":
        case "flowering":
            return "growing";
        case "fruiting":
        case "harvest":
            return "mature";
        case "finished":
            return "finished";
        default:
            return "growing";
    }
}

// --------------------------------------------------------------------------
// Palette tokens (earthy, on-brand)
// --------------------------------------------------------------------------

const TOKENS = {
    leaf: "#6B8C5C",        // sage
    leafDark: "#4A6B3A",
    soil: "#8B6F47",
    soilDark: "#5A4732",
    stem: "#5A7A3C",
    fruitRed: "#C06547",     // terracotta
    fruitYellow: "#D4A04C",  // golden
    fruitPurple: "#7B5E7A",
    fruitGreen: "#9BA84E",
    berry: "#B8421F",
    root: "#D07C3E",         // carrot orange
    bulb: "#E8D9A5",
    flowerYellow: "#E8B84C",
    flowerOrange: "#D4814C",
    flowerPink: "#C98BA3",
};

// Shared ground line for grounded stages
const Ground = () => (
    <line x1="2" y1="20" x2="22" y2="20" stroke={TOKENS.soil} strokeWidth="1.2" strokeLinecap="round" opacity="0.55" />
);

// Seed/sown — same for every crop (a seed tucked in soil)
function SeedIcon() {
    return (
        <g>
            <Ground />
            <ellipse cx="12" cy="19" rx="1.8" ry="1.1" fill={TOKENS.soilDark} />
            <path d="M9 21 Q12 22 15 21" stroke={TOKENS.soilDark} strokeWidth="0.9" fill="none" strokeLinecap="round" opacity="0.6" />
        </g>
    );
}

// Sprout — tiny 2-leaf cotyledon, same silhouette across crops
function SproutIcon() {
    return (
        <g>
            <Ground />
            <path d="M12 20 L12 14" stroke={TOKENS.stem} strokeWidth="1.2" strokeLinecap="round" />
            <path d="M12 15 Q8 13 7 15 Q9 16 12 15" fill={TOKENS.leaf} />
            <path d="M12 15 Q16 13 17 15 Q15 16 12 15" fill={TOKENS.leaf} />
            <circle cx="12" cy="14" r="0.6" fill={TOKENS.leafDark} />
        </g>
    );
}

// --------------------------------------------------------------------------
// Visual-key renderers — "growing" and "mature" variants
// --------------------------------------------------------------------------

// Generic growing plant (three leaves fanning out from central stem)
function Leaves({ tight = false }) {
    const y1 = tight ? 10 : 8;
    return (
        <g>
            <path d={`M12 20 L12 ${y1}`} stroke={TOKENS.stem} strokeWidth="1.3" strokeLinecap="round" />
            <path d={`M12 ${y1 + 2} Q6 ${y1 + 2} 5 ${y1 + 5} Q9 ${y1 + 5} 12 ${y1 + 3}`} fill={TOKENS.leaf} />
            <path d={`M12 ${y1 + 2} Q18 ${y1 + 2} 19 ${y1 + 5} Q15 ${y1 + 5} 12 ${y1 + 3}`} fill={TOKENS.leafDark} />
            <path d={`M12 ${y1 - 1} Q9 ${y1 - 4} 11 ${y1 - 4} Q13 ${y1 - 2} 12 ${y1}`} fill={TOKENS.leaf} />
            <path d={`M12 ${y1 - 1} Q15 ${y1 - 4} 13 ${y1 - 4} Q13 ${y1 - 2} 12 ${y1}`} fill={TOKENS.leafDark} />
        </g>
    );
}

const VISUALS = {
    "fruit-round": {
        growing: () => <g><Ground /><Leaves /></g>,
        mature: () => (
            <g>
                <Ground />
                <Leaves />
                <circle cx="8" cy="14" r="2.2" fill={TOKENS.fruitRed} />
                <circle cx="16" cy="15" r="2.4" fill={TOKENS.fruitRed} />
                <path d="M8 12.2 L8 11.2" stroke={TOKENS.stem} strokeWidth="0.6" />
                <path d="M16 12.8 L16 11.8" stroke={TOKENS.stem} strokeWidth="0.6" />
            </g>
        ),
    },
    "fruit-long": {
        growing: () => <g><Ground /><Leaves /></g>,
        mature: () => (
            <g>
                <Ground />
                <Leaves />
                <path d="M7 13 Q6 18 8 18 Q10 17 9 13 Z" fill={TOKENS.fruitGreen} />
                <path d="M15 12 Q13 18 16 18 Q18 17 17 12 Z" fill={TOKENS.fruitGreen} />
                <circle cx="7.5" cy="13" r="0.5" fill={TOKENS.stem} />
                <circle cx="16" cy="12" r="0.5" fill={TOKENS.stem} />
            </g>
        ),
    },
    "vine-fruit": {
        growing: () => (
            <g>
                <Ground />
                <path d="M4 20 Q8 16 12 18 Q16 20 20 16" stroke={TOKENS.stem} strokeWidth="1.2" fill="none" strokeLinecap="round" />
                <path d="M7 15 Q4 13 6 11 Q9 12 7 15" fill={TOKENS.leaf} />
                <path d="M17 13 Q14 11 16 9 Q19 10 17 13" fill={TOKENS.leafDark} />
            </g>
        ),
        mature: () => (
            <g>
                <Ground />
                <path d="M4 20 Q8 16 12 18 Q16 20 20 16" stroke={TOKENS.stem} strokeWidth="1.2" fill="none" strokeLinecap="round" />
                <path d="M7 15 Q4 13 6 11 Q9 12 7 15" fill={TOKENS.leaf} />
                <path d="M17 13 Q14 11 16 9 Q19 10 17 13" fill={TOKENS.leafDark} />
                <ellipse cx="12" cy="18" rx="3.5" ry="2.8" fill={TOKENS.flowerOrange} />
                <path d="M12 16 L12 15" stroke={TOKENS.stem} strokeWidth="0.7" />
            </g>
        ),
    },
    "root-long": {
        growing: () => (
            <g>
                <Ground />
                <path d="M12 20 L12 10" stroke={TOKENS.stem} strokeWidth="1" />
                <path d="M12 14 Q7 10 8 7 Q11 9 12 14" fill={TOKENS.leaf} />
                <path d="M12 14 Q17 10 16 7 Q13 9 12 14" fill={TOKENS.leafDark} />
                <path d="M12 13 Q10 6 11 4 Q13 7 12 13" fill={TOKENS.leaf} />
            </g>
        ),
        mature: () => (
            <g>
                <Ground />
                <path d="M12 14 Q7 10 8 7 Q11 9 12 14" fill={TOKENS.leaf} />
                <path d="M12 14 Q17 10 16 7 Q13 9 12 14" fill={TOKENS.leafDark} />
                <path d="M12 13 Q10 6 11 4 Q13 7 12 13" fill={TOKENS.leaf} />
                <path d="M10 14 Q9 20 12 22 Q15 20 14 14 Z" fill={TOKENS.root} />
                <path d="M11 17 L10 18 M13 17 L14 18 M11.5 19 L11 20 M12.5 19 L13 20" stroke={TOKENS.root} strokeWidth="0.4" opacity="0.7" />
            </g>
        ),
    },
    "root-round": {
        growing: () => (
            <g>
                <Ground />
                <path d="M12 16 L8 7 M12 16 L12 5 M12 16 L16 7" stroke={TOKENS.leaf} strokeWidth="1.1" strokeLinecap="round" />
            </g>
        ),
        mature: () => (
            <g>
                <Ground />
                <path d="M12 16 L8 7 M12 16 L12 5 M12 16 L16 7" stroke={TOKENS.leaf} strokeWidth="1.1" strokeLinecap="round" />
                <ellipse cx="12" cy="18" rx="4" ry="3.2" fill={TOKENS.fruitPurple} />
                <path d="M12 15.5 L12 14.5" stroke={TOKENS.stem} strokeWidth="0.5" />
            </g>
        ),
    },
    "leafy-head": {
        growing: () => (
            <g>
                <Ground />
                <path d="M5 19 Q5 12 12 11 Q19 12 19 19 Z" fill={TOKENS.leaf} />
                <path d="M12 19 L12 11" stroke={TOKENS.leafDark} strokeWidth="0.6" opacity="0.5" />
            </g>
        ),
        mature: () => (
            <g>
                <Ground />
                <ellipse cx="12" cy="15" rx="7" ry="5" fill={TOKENS.leaf} />
                <path d="M8 13 Q12 10 16 13" stroke={TOKENS.leafDark} strokeWidth="0.7" fill="none" />
                <path d="M6 16 Q12 13 18 16" stroke={TOKENS.leafDark} strokeWidth="0.6" fill="none" opacity="0.6" />
                <path d="M12 10 L12 16" stroke={TOKENS.leafDark} strokeWidth="0.5" opacity="0.5" />
            </g>
        ),
    },
    "leafy-loose": {
        growing: () => (
            <g>
                <Ground />
                <path d="M12 20 L12 12" stroke={TOKENS.stem} strokeWidth="1" />
                <path d="M12 15 Q6 12 4 14 Q7 17 12 15" fill={TOKENS.leaf} />
                <path d="M12 13 Q18 10 20 12 Q17 15 12 13" fill={TOKENS.leafDark} />
                <path d="M12 11 Q8 8 10 5 Q13 8 12 11" fill={TOKENS.leaf} />
                <path d="M12 12 Q16 8 18 6 Q15 11 12 12" fill={TOKENS.leafDark} />
            </g>
        ),
        mature: () => (
            <g>
                <Ground />
                <path d="M12 20 L12 10" stroke={TOKENS.stem} strokeWidth="1" />
                <path d="M12 17 Q5 13 3 15 Q6 19 12 17" fill={TOKENS.leaf} />
                <path d="M12 15 Q19 11 21 13 Q18 17 12 15" fill={TOKENS.leafDark} />
                <path d="M12 13 Q6 10 5 6 Q10 9 12 13" fill={TOKENS.leaf} />
                <path d="M12 11 Q18 8 19 4 Q15 9 12 11" fill={TOKENS.leafDark} />
                <path d="M12 10 Q10 5 11 3 Q13 6 12 10" fill={TOKENS.leaf} />
            </g>
        ),
    },
    "branching": {
        growing: () => (
            <g>
                <Ground />
                <path d="M12 20 L12 8" stroke={TOKENS.stem} strokeWidth="1.3" />
                <path d="M12 16 L7 14 M12 14 L17 12 M12 12 L8 9" stroke={TOKENS.leaf} strokeWidth="1.1" strokeLinecap="round" />
            </g>
        ),
        mature: () => (
            <g>
                <Ground />
                <path d="M12 20 L12 10" stroke={TOKENS.stem} strokeWidth="1.3" />
                <circle cx="12" cy="9" r="4" fill={TOKENS.leaf} />
                <circle cx="10" cy="7.5" r="1.2" fill={TOKENS.leafDark} />
                <circle cx="14" cy="8" r="1.2" fill={TOKENS.leafDark} />
                <circle cx="12" cy="11" r="1.3" fill={TOKENS.leafDark} />
                <circle cx="9" cy="10" r="1.1" fill={TOKENS.leafDark} />
                <circle cx="15" cy="10.5" r="1.1" fill={TOKENS.leafDark} />
            </g>
        ),
    },
    "bulb": {
        growing: () => (
            <g>
                <Ground />
                <path d="M10 20 L9 7 M12 20 L12 5 M14 20 L15 7" stroke={TOKENS.leaf} strokeWidth="1.1" strokeLinecap="round" />
            </g>
        ),
        mature: () => (
            <g>
                <Ground />
                <path d="M10 20 L9 7 M12 20 L12 5 M14 20 L15 7" stroke={TOKENS.leaf} strokeWidth="1.1" strokeLinecap="round" />
                <path d="M8 18 Q8 14 12 14 Q16 14 16 18 Q16 21 12 21 Q8 21 8 18 Z" fill={TOKENS.bulb} />
                <path d="M12 14 L12 21" stroke={TOKENS.soilDark} strokeWidth="0.4" opacity="0.5" />
            </g>
        ),
    },
    "stalk": {
        growing: () => (
            <g>
                <Ground />
                <path d="M11 20 L10 10 M13 20 L14 10" stroke={TOKENS.leaf} strokeWidth="1.4" strokeLinecap="round" />
                <path d="M10 10 Q8 6 9 4 M14 10 Q16 6 15 4" stroke={TOKENS.leaf} strokeWidth="1.1" strokeLinecap="round" fill="none" />
            </g>
        ),
        mature: () => (
            <g>
                <Ground />
                <path d="M11 20 L10 8 M13 20 L14 8" stroke={TOKENS.bulb} strokeWidth="2.2" strokeLinecap="round" />
                <path d="M10 8 Q6 4 8 2 M14 8 Q18 4 16 2 M12 8 L12 1" stroke={TOKENS.leaf} strokeWidth="1.2" strokeLinecap="round" fill="none" />
            </g>
        ),
    },
    "legume": {
        growing: () => (
            <g>
                <Ground />
                <path d="M12 20 L12 6" stroke={TOKENS.stem} strokeWidth="1.1" />
                <path d="M12 15 Q18 13 19 10 Q15 11 12 15" fill={TOKENS.leafDark} />
                <path d="M12 10 Q6 8 5 5 Q10 6 12 10" fill={TOKENS.leaf} />
                <path d="M12 8 Q16 5 17 3 Q14 4 12 8" fill={TOKENS.leaf} />
            </g>
        ),
        mature: () => (
            <g>
                <Ground />
                <path d="M12 20 L12 5" stroke={TOKENS.stem} strokeWidth="1.1" />
                <path d="M12 15 Q18 13 19 10 Q15 11 12 15" fill={TOKENS.leafDark} />
                <path d="M12 10 Q6 8 5 5 Q10 6 12 10" fill={TOKENS.leaf} />
                <path d="M15 17 Q17 12 19 16 Q17 18 15 17 Z" fill={TOKENS.fruitGreen} />
                <circle cx="16.5" cy="14.5" r="0.5" fill={TOKENS.leafDark} />
                <circle cx="17" cy="15.8" r="0.5" fill={TOKENS.leafDark} />
                <circle cx="17.5" cy="16.7" r="0.5" fill={TOKENS.leafDark} />
            </g>
        ),
    },
    "potato": {
        growing: () => (
            <g>
                <Ground />
                <path d="M12 20 L12 8" stroke={TOKENS.stem} strokeWidth="1" />
                <path d="M12 12 Q6 11 5 8 Q9 9 12 12" fill={TOKENS.leaf} />
                <path d="M12 10 Q18 9 19 6 Q15 7 12 10" fill={TOKENS.leafDark} />
                <path d="M12 14 Q9 11 10 7 Q13 10 12 14" fill={TOKENS.leaf} />
            </g>
        ),
        mature: () => (
            <g>
                <Ground />
                <path d="M12 20 L12 8" stroke={TOKENS.stem} strokeWidth="1" />
                <path d="M12 12 Q6 11 5 8 Q9 9 12 12" fill={TOKENS.leaf} />
                <path d="M12 10 Q18 9 19 6 Q15 7 12 10" fill={TOKENS.leafDark} />
                <ellipse cx="7" cy="21" rx="1.8" ry="1.2" fill={TOKENS.soil} />
                <ellipse cx="12" cy="22" rx="2.0" ry="1.2" fill={TOKENS.soil} />
                <ellipse cx="17" cy="21" rx="1.8" ry="1.2" fill={TOKENS.soilDark} />
            </g>
        ),
    },
    "herb-small": {
        growing: () => (
            <g>
                <Ground />
                <path d="M12 20 L12 10" stroke={TOKENS.stem} strokeWidth="1" />
                <circle cx="9" cy="12" r="1.6" fill={TOKENS.leaf} />
                <circle cx="15" cy="12" r="1.6" fill={TOKENS.leafDark} />
                <circle cx="12" cy="9" r="1.6" fill={TOKENS.leaf} />
            </g>
        ),
        mature: () => (
            <g>
                <Ground />
                <path d="M12 20 L12 8" stroke={TOKENS.stem} strokeWidth="1" />
                <circle cx="8" cy="12" r="2" fill={TOKENS.leaf} />
                <circle cx="16" cy="12" r="2" fill={TOKENS.leafDark} />
                <circle cx="12" cy="8" r="2" fill={TOKENS.leaf} />
                <circle cx="10" cy="10" r="1.3" fill={TOKENS.leafDark} />
                <circle cx="14" cy="10" r="1.3" fill={TOKENS.leaf} />
            </g>
        ),
    },
    "herb-woody": {
        growing: () => (
            <g>
                <Ground />
                <path d="M12 20 L12 9" stroke={TOKENS.soil} strokeWidth="1.1" />
                <path d="M12 16 L8 13 M12 14 L16 11 M12 12 L9 8 M12 11 L15 8" stroke={TOKENS.leafDark} strokeWidth="0.9" strokeLinecap="round" />
            </g>
        ),
        mature: () => (
            <g>
                <Ground />
                <path d="M12 20 L12 8" stroke={TOKENS.soil} strokeWidth="1.1" />
                <path d="M12 16 L7 13 M12 14 L17 10 M12 12 L8 7 M12 10 L15 6 M12 18 L9 17 M12 18 L15 17" stroke={TOKENS.leafDark} strokeWidth="1" strokeLinecap="round" />
                <circle cx="8" cy="8" r="0.7" fill={TOKENS.fruitPurple} />
                <circle cx="16" cy="10" r="0.6" fill={TOKENS.fruitPurple} />
            </g>
        ),
    },
    "flower": {
        growing: () => (
            <g>
                <Ground />
                <path d="M12 20 L12 9" stroke={TOKENS.stem} strokeWidth="1.2" />
                <path d="M12 15 Q7 13 6 15 Q8 17 12 15" fill={TOKENS.leaf} />
                <path d="M12 12 Q17 10 18 12 Q16 14 12 12" fill={TOKENS.leafDark} />
                <circle cx="12" cy="9" r="1.4" fill={TOKENS.flowerYellow} />
            </g>
        ),
        mature: () => (
            <g>
                <Ground />
                <path d="M12 20 L12 11" stroke={TOKENS.stem} strokeWidth="1.2" />
                <path d="M12 16 Q7 14 6 16 Q8 18 12 16" fill={TOKENS.leaf} />
                <path d="M12 13 Q17 11 18 13 Q16 15 12 13" fill={TOKENS.leafDark} />
                <circle cx="12" cy="8" r="3" fill={TOKENS.flowerOrange} />
                <circle cx="12" cy="8" r="1.5" fill={TOKENS.flowerYellow} />
                <circle cx="9" cy="6" r="1.1" fill={TOKENS.flowerOrange} opacity="0.85" />
                <circle cx="15" cy="6" r="1.1" fill={TOKENS.flowerOrange} opacity="0.85" />
                <circle cx="9" cy="10" r="1.1" fill={TOKENS.flowerOrange} opacity="0.85" />
                <circle cx="15" cy="10" r="1.1" fill={TOKENS.flowerOrange} opacity="0.85" />
            </g>
        ),
    },
    "berry": {
        growing: () => (
            <g>
                <Ground />
                <path d="M12 20 L12 10" stroke={TOKENS.stem} strokeWidth="1" />
                <path d="M12 13 Q7 11 6 13 Q8 15 12 13" fill={TOKENS.leaf} />
                <path d="M12 12 Q17 10 18 12 Q16 14 12 12" fill={TOKENS.leafDark} />
                <path d="M12 10 Q10 7 11 5 Q13 7 12 10" fill={TOKENS.leaf} />
            </g>
        ),
        mature: () => (
            <g>
                <Ground />
                <path d="M12 20 L12 10" stroke={TOKENS.stem} strokeWidth="1" />
                <path d="M12 13 Q7 11 6 13 Q8 15 12 13" fill={TOKENS.leaf} />
                <path d="M12 12 Q17 10 18 12 Q16 14 12 12" fill={TOKENS.leafDark} />
                <circle cx="8" cy="17" r="1.4" fill={TOKENS.berry} />
                <circle cx="12" cy="18" r="1.6" fill={TOKENS.berry} />
                <circle cx="16" cy="17" r="1.4" fill={TOKENS.berry} />
                <path d="M8 15.8 L8 15.2 M12 16.6 L12 16 M16 15.8 L16 15.2" stroke={TOKENS.stem} strokeWidth="0.5" />
            </g>
        ),
    },
    "rhubarb": {
        growing: () => (
            <g>
                <Ground />
                <path d="M10 20 L10 14 M12 20 L12 12 M14 20 L14 14" stroke={TOKENS.berry} strokeWidth="1.4" strokeLinecap="round" />
                <path d="M10 14 Q6 10 7 8 Q11 11 10 14" fill={TOKENS.leaf} />
                <path d="M12 12 Q9 7 11 5 Q14 8 12 12" fill={TOKENS.leafDark} />
                <path d="M14 14 Q18 10 17 8 Q13 11 14 14" fill={TOKENS.leaf} />
            </g>
        ),
        mature: () => (
            <g>
                <Ground />
                <path d="M9 20 L9 12 M12 20 L12 10 M15 20 L15 12" stroke={TOKENS.berry} strokeWidth="1.6" strokeLinecap="round" />
                <path d="M9 12 Q3 8 5 6 Q10 9 9 12" fill={TOKENS.leaf} />
                <path d="M12 10 Q8 4 11 2 Q15 6 12 10" fill={TOKENS.leafDark} />
                <path d="M15 12 Q21 8 19 6 Q14 9 15 12" fill={TOKENS.leaf} />
            </g>
        ),
    },
    "generic": {
        growing: () => <g><Ground /><Leaves /></g>,
        mature: () => (
            <g>
                <Ground />
                <Leaves />
                <circle cx="12" cy="12" r="1.4" fill={TOKENS.fruitYellow} />
            </g>
        ),
    },
};

// --------------------------------------------------------------------------
// Painterly catalog — when a watercolour PNG exists at
// `/brand/veggies/<slug>-<bucket>.png` we render that instead of the SVG.
// The slug map covers the catalog keys used by `/app/backend/crop_knowledge.py`
// — for any crop_name that doesn't resolve to a known slug we fall back
// straight to the SVG. Failed image loads also gracefully degrade.
// --------------------------------------------------------------------------

// Common-name (lowercase, trimmed) → catalog slug used in PNG filenames.
const CROP_TO_PAINTERLY_SLUG = {
    "tomato": "tomato",
    "cherry tomato": "tomato",
    "lettuce": "lettuce",
    "carrot": "carrot",
    "broccoli": "broccoli",
    "kale": "kale",
    "potato": "potato",
    "onion": "onion",
    "garlic": "garlic",
    "courgette": "courgette",
    "zucchini": "courgette",
    "runner bean": "bean_runner",
    "pea": "pea",
    "sugar snap pea": "pea",
    "beetroot": "beetroot",
    "beet": "beetroot",
    "spinach": "spinach",
    "radish": "radish",
    "calendula": "calendula",
    "cabbage": "cabbage",
    "cauliflower": "cauliflower",
    "brussels sprouts": "brussels_sprouts",
    "pak choi": "pak_choi",
    "bok choy": "pak_choi",
    "kohlrabi": "kohlrabi",
    "swede": "swede",
    "mizuna": "mizuna",
    "rocket": "rocket",
    "arugula": "rocket",
    "leek": "leek",
    "shallot": "shallot",
    "spring onion": "spring_onion",
    "chive": "chive",
    "pepper": "pepper",
    "chili": "chili",
    "chilli": "chili",
    "cucumber": "cucumber",
    "pumpkin": "pumpkin",
    "winter squash": "squash_winter",
    "squash": "squash_winter",
    "broad bean": "broad_bean",
    "french bean": "french_bean",
    "bean": "french_bean",
    "parsnip": "parsnip",
    "celery": "celery",
    "celeriac": "celeriac",
    "fennel": "fennel",
    "dill": "dill",
    "parsley": "parsley",
    "coriander": "coriander",
    "cilantro": "coriander",
    "chard": "chard",
    "swiss chard": "chard",
    "sorrel": "sorrel",
    "perpetual spinach": "perpetual_spinach",
    "lamb's lettuce": "lambs_lettuce",
    "lambs lettuce": "lambs_lettuce",
    "basil": "basil",
    "thyme": "thyme",
    "rosemary": "rosemary",
    "sage": "sage",
    "mint": "mint",
    "oregano": "oregano",
    "marjoram": "oregano",
    "strawberry": "strawberry",
    "raspberry": "raspberry",
    "rhubarb": "rhubarb",
    "nasturtium": "nasturtium",
    "borage": "borage",
    "sunflower": "sunflower",
    "marigold": "calendula",
};

function painterlySlug(cropName) {
    if (!cropName) return null;
    const key = cropName.toLowerCase().trim();
    return CROP_TO_PAINTERLY_SLUG[key] || CROP_TO_PAINTERLY_SLUG[key.split(/\s+/)[0]] || null;
}

// In-process flag so we don't keep firing 404s for slugs we already
// know are missing. Resets on hard reload.
const _painterlyMissing = new Set();

/**
 * <VeggieIcon cropName="tomato" stage="fruiting" size={24} />
 *
 * Renders the painterly watercolour PNG when available (sprout / growing /
 * mature buckets), gracefully falling back to the flat SVG illustration
 * otherwise. The ``svgOnly`` prop forces the legacy SVG path even when a
 * painterly image exists — used by the side-by-side preview page.
 */
export default function VeggieIcon({
    cropName,
    stage = "vegetative",
    size = 24,
    className = "",
    title,
    svgOnly = false,
}) {
    const key = visualKeyForCrop(cropName);
    const bucket = stageBucket(stage);
    const visual = VISUALS[key] || VISUALS.generic;

    // Try the painterly catalog first (sprout / growing / mature only —
    // the seed bucket is identical across crops and stays as flat SVG).
    const slug = painterlySlug(cropName);
    const paintBucket = bucket === "finished" ? "mature" : bucket;
    const cacheKey = slug ? `${slug}-${paintBucket}` : null;
    const [painterlyFailed, setPainterlyFailed] = useState(
        cacheKey ? _painterlyMissing.has(cacheKey) : true,
    );
    const usePainterly =
        !svgOnly &&
        slug &&
        ["sprout", "growing", "mature"].includes(paintBucket) &&
        !painterlyFailed;

    if (usePainterly) {
        const src = `/brand/veggies/${cacheKey}.png`;
        return (
            <img
                src={src}
                alt={title || `${cropName || "crop"} at ${stage}`}
                width={size}
                height={size}
                style={{ width: size, height: size, objectFit: "cover" }}
                className={`rounded-sm ${className} ${bucket === "finished" ? "opacity-60" : ""}`}
                data-testid={`veggie-icon-${key}-${bucket}`}
                onError={() => {
                    if (cacheKey) _painterlyMissing.add(cacheKey);
                    setPainterlyFailed(true);
                }}
                draggable={false}
            />
        );
    }

    // SVG fallback path
    let content;
    if (bucket === "seed") content = <SeedIcon />;
    else if (bucket === "sprout") content = <SproutIcon />;
    else if (bucket === "finished") {
        const inner = visual.mature ? visual.mature() : visual.growing();
        content = <g opacity="0.45">{inner}</g>;
    } else if (bucket === "mature") content = visual.mature ? visual.mature() : visual.growing();
    else content = visual.growing();

    return (
        <svg
            width={size}
            height={size}
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
            className={className}
            data-testid={`veggie-icon-${key}-${bucket}`}
            role="img"
            aria-label={title || `${cropName || "crop"} at ${stage}`}
        >
            {title ? <title>{title}</title> : null}
            {content}
        </svg>
    );
}
